import { reportApiError } from '@/datadog/report-api-error';
import { isRequestCancelled } from '@/datadog/is-request-cancelled';
import { ERROR_STATUS_CODES_TO_IGNORE } from '@/requests/api/constants';
import { getQueryClient } from '@/providers/queryClient';
import { getSessionValues } from '@/providers/session-provider/get-session-values';
import { BASE_BACKEND_URL } from '@/requests/constants';
import {
  createAndSetClient,
  createApiQuery,
  mergeHeaders,
  NetworkError,
} from '@pigello/construct-api';
import { getAuthHeaders } from './get-auth-headers';
import { createMotivationAwareFetch } from './motivation-retry';

type RequestOptions = NonNullable<
  Parameters<typeof createAndSetClient>[0]['defaults']
>;

function getRequestSignal(request: string | Request, options: RequestOptions) {
  if (options.signal != null) return options.signal;
  if (typeof request !== 'string') return request.signal;
}

function willRetryNetworkRequest(options: RequestOptions, error?: Error) {
  // ofetch runs onRequestError before retrying and uses status 500 for network
  // failures. Mirror its defaults without changing retry behavior.
  if (error?.name === 'AbortError' && !options.timeout) return false;
  if (options.retryStatusCodes && !options.retryStatusCodes.includes(500))
    return false;
  if (options.retry === false) return false;
  if (options.retry != null) return options.retry > 0;
  return !['PATCH', 'POST', 'PUT', 'DELETE'].includes(
    (options.method ?? 'GET').toUpperCase()
  );
}

export const { api, client } = createAndSetClient({
  config: {
    getOrganizationId: () => getSessionValues().organizationId,
  },
  globalOptions: {
    // Resolve fetch at call time so later global patches (msw, monitoring)
    // still apply to construct traffic.
    fetch: createMotivationAwareFetch((input, init) =>
      globalThis.fetch(input, init)
    ),
  },
  defaults: {
    onRequest: (ctx) => {
      ctx.options.baseURL = BASE_BACKEND_URL;
      const session = getSessionValues();
      const authHeaders = getAuthHeaders({ session });
      ctx.options.headers = mergeHeaders(authHeaders, ctx.options.headers);
    },
    onRequestError: (ctx) => {
      const signal = getRequestSignal(ctx.request, ctx.options);
      if (
        !isRequestCancelled(ctx.error, signal) &&
        !willRetryNetworkRequest(ctx.options, ctx.error)
      ) {
        reportApiError({
          source: 'construct-api',
          url: typeof ctx.request === 'string' ? ctx.request : ctx.request.url,
          method: ctx.options.method ?? 'GET',
          status: 0,
        });
      }
    },
    onResponseError: (ctx) => {
      if (!ERROR_STATUS_CODES_TO_IGNORE.has(ctx.response.status)) {
        reportApiError({
          source: 'construct-api',
          url:
            ctx.response.url ||
            (typeof ctx.request === 'string' ? ctx.request : ctx.request.url),
          method: ctx.options.method ?? 'GET',
          status: ctx.response.status,
          details: ctx.response._data,
        });
      }
      throw new NetworkError({
        message: ctx.response.statusText,
        status: ctx.response.status,
        url: ctx.response.url,
        details: ctx.response._data,
      });
    },
  },
});

export const {
  query,
  useApiQuery,
  useInfiniteApiQuery,
  fetchApiQuery,
  defineInclude,
} = createApiQuery({
  api,
  getQueryClient,
});
